import {
  entitlementBlockerDefaultUserMessage,
  isEntitlementBlockerCode,
} from "@/features/billing/entitlement-reason-codes"
import type { EntitlementBlocker } from "@/features/billing/entitlement-types"

/** JSON `error` object for failed API responses (required `code` + `message`). */
export type ApiErrorPayload = {
  code: string
  message: string
  blockers?: readonly EntitlementBlocker[]
}

export type ApiMeta = Record<string, unknown>

export type ApiSuccess<T> = { data: T; error: null; meta?: ApiMeta }

export type ApiFailure = { error: ApiErrorPayload; meta?: ApiMeta }

export type ApiEnvelope<T> = ApiSuccess<T> | ApiFailure

export function isApiFailureBody(body: unknown): body is ApiFailure {
  if (body === null || typeof body !== "object") return false
  const e = (body as { error?: unknown }).error
  return (
    typeof e === "object" &&
    e !== null &&
    typeof (e as { code?: unknown }).code === "string" &&
    typeof (e as { message?: unknown }).message === "string"
  )
}

export function isApiSuccessBody<T>(body: unknown): body is ApiSuccess<T> {
  return (
    body !== null &&
    typeof body === "object" &&
    "data" in body &&
    "error" in body &&
    (body as { error: unknown }).error === null
  )
}

/** Message from a failed API JSON body, or `fallback` when the body is not a standard error envelope. */
export function readApiErrorMessage(body: unknown, fallback: string): string {
  if (!isApiFailureBody(body)) return fallback
  const c = body.error.code
  const msg = body.error.message?.trim()
  if (isEntitlementBlockerCode(c)) {
    return msg || entitlementBlockerDefaultUserMessage(c)
  }
  return msg || fallback
}
